import moment from 'moment';

/**
 * Normalise a chase-list deadline string into a canonical `DD MMM YYYY`
 * format (e.g. "05 Nov 2026"). Chase lists arrive with wildly inconsistent
 * date formats — "5/11", "5 Nov", "5th November", "Nov 5", "2026-11-05",
 * "5-Nov-26" — and often omit the year entirely because the campaign year is
 * implicit from context. This helper standardises the display so every row
 * looks the same and downstream code can rely on a single format.
 *
 * Year inference: if the input string doesn't include a year, we assume the
 * current calendar year. If the resulting date is already in the past, we
 * roll forward to next year — a "5 Nov" deadline on a chase list uploaded in
 * December almost certainly means next year's November.
 *
 * If the input can't be parsed as a date (e.g. "TBA", "ASAP", or a garbled
 * string), the original value is returned unchanged so user intent isn't
 * destroyed.
 *
 * `today` is injectable so tests don't drift with the wall clock.
 */
export const normaliseDeadlineDate = (
  input: string | null | undefined,
  today: moment.Moment = moment().startOf('day'),
): string => {
  if (!input) return '';
  const raw = String(input).trim();
  if (!raw) return '';

  // Formats that already include a year (4- or 2-digit). Ordered so more
  // specific / less ambiguous patterns come first. `DD/MM/YYYY` is listed
  // before `MM/DD/YYYY` because the SA/UK convention is day-first — a
  // chase list saying "05/11" almost always means 5 November, not 11 May.
  const formatsWithYear = [
    'YYYY-MM-DD',
    'YYYY/MM/DD',
    'DD/MM/YYYY', 'D/M/YYYY',
    'DD-MM-YYYY', 'D-M-YYYY',
    'DD.MM.YYYY', 'D.M.YYYY',
    'DD MMM YYYY', 'D MMM YYYY',
    'DD MMMM YYYY', 'D MMMM YYYY',
    'DD-MMM-YYYY', 'D-MMM-YYYY',
    'DD-MMMM-YYYY', 'D-MMMM-YYYY',
    'MMM DD YYYY', 'MMM D YYYY',
    'MMMM DD YYYY', 'MMMM D YYYY',
    'MMM DD, YYYY', 'MMM D, YYYY',
    'MMMM DD, YYYY', 'MMMM D, YYYY',
    'Do MMMM YYYY', 'Do MMM YYYY',
    'MMM Do YYYY', 'MMMM Do YYYY',
    // 2-digit years
    'DD/MM/YY', 'D/M/YY',
    'DD-MM-YY', 'D-M-YY',
    'DD MMM YY', 'D MMM YY',
    'DD-MMM-YY', 'D-MMM-YY',
  ];

  // Formats without a year — the campaign year has to be inferred.
  const formatsNoYear = [
    'DD/MM', 'D/M',
    'DD-MM', 'D-M',
    'DD.MM', 'D.M',
    'DD MMM', 'D MMM',
    'DD MMMM', 'D MMMM',
    'DD-MMM', 'D-MMM',
    'DD-MMMM', 'D-MMMM',
    'MMM DD', 'MMM D',
    'MMMM DD', 'MMMM D',
    'Do MMMM', 'Do MMM',
    'MMM Do', 'MMMM Do',
  ];

  const OUTPUT = 'DD MMM YYYY';

  // Try year-containing formats first — if these match, we trust the year
  // verbatim (a chase list saying "5 Nov 2024" means 2024 even if that's
  // in the past).
  const withYear = moment(raw, formatsWithYear, true);
  if (withYear.isValid()) return withYear.format(OUTPUT);

  // No year in the input — infer from `today`. If the resulting date is
  // already in the past, roll to next year.
  const withoutYear = moment(raw, formatsNoYear, true);
  if (withoutYear.isValid()) {
    withoutYear.year(today.year());
    if (withoutYear.isBefore(today)) withoutYear.year(today.year() + 1);
    return withoutYear.format(OUTPUT);
  }

  // Unparseable — return the original so the user can see + fix it in the
  // review table rather than losing it.
  return raw;
};

/**
 * Normalise a chase-list "period" field into a canonical
 * `DD MMM YYYY - DD MMM YYYY` range. Chase-list periods arrive in messy
 * fragmented forms — e.g. "08 - 13 June, 15 - 20 June, 22 - 27 June,
 * 29 June - 04 July, 06 - 11 July, 13 - 18 July" — which is unpleasant
 * to read at a glance and impossible to sort. This helper pulls every
 * date-like token out of the string, finds the min and max, and returns
 * a clean first-to-last summary.
 *
 * Strategy:
 *  1. Scan the string for tokens that could be dates ("08 June",
 *     "13 July 2026", "5/11", "08 Jun 2026", etc.).
 *  2. Parse each token — tokens WITH an explicit year keep that year;
 *     tokens WITHOUT a year are assigned the current year TENTATIVELY.
 *  3. Adjust the range as a COHERENT campaign (not date-by-date). The
 *     rule is: all dates belong to one campaign period, so they should
 *     share a common year. If any token has an explicit year, use it as
 *     the base for the whole range. If no explicit years and the LATEST
 *     date is in the past, roll every date forward one year together.
 *  4. Handle year-crossing periods (rare): a Dec → Jan campaign gets
 *     detected by walking the tokens in input order — if the next
 *     date's calendar position falls BEFORE the previous, it's a year
 *     rollover and gets +1 year.
 *  5. For fragmented ranges like "08 - 13 June", the bare "08" has no
 *     month attached. Pass 2 borrows the month/year from the nearest
 *     parseable neighbour.
 *  6. Sort, take min and max, return `DD MMM YYYY - DD MMM YYYY`. If
 *     only one date parsed, return that. If nothing parsed, return the
 *     input verbatim so user intent isn't destroyed.
 */
export const normaliseCampaignPeriod = (
  input: string | null | undefined,
  today: moment.Moment = moment().startOf('day'),
): string => {
  if (!input) return '';
  const raw = String(input).trim();
  if (!raw) return '';

  // Format lists reused from normaliseDeadlineDate — kept local so
  // we can distinguish "parsed with explicit year" vs "parsed without
  // year". If those two lists drift, extract into a shared constant.
  const formatsWithYear = [
    'YYYY-MM-DD', 'YYYY/MM/DD',
    'DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY', 'DD.MM.YYYY', 'D.M.YYYY',
    'DD MMM YYYY', 'D MMM YYYY', 'DD MMMM YYYY', 'D MMMM YYYY',
    'DD-MMM-YYYY', 'D-MMM-YYYY', 'DD-MMMM-YYYY', 'D-MMMM-YYYY',
    'MMM DD YYYY', 'MMM D YYYY', 'MMMM DD YYYY', 'MMMM D YYYY',
    'MMM DD, YYYY', 'MMM D, YYYY', 'MMMM DD, YYYY', 'MMMM D, YYYY',
    'Do MMMM YYYY', 'Do MMM YYYY', 'MMM Do YYYY', 'MMMM Do YYYY',
    'DD/MM/YY', 'D/M/YY', 'DD-MM-YY', 'D-M-YY',
    'DD MMM YY', 'D MMM YY', 'DD-MMM-YY', 'D-MMM-YY',
  ];
  const formatsNoYear = [
    'DD/MM', 'D/M', 'DD-MM', 'D-M', 'DD.MM', 'D.M',
    'DD MMM', 'D MMM', 'DD MMMM', 'D MMMM',
    'DD-MMM', 'D-MMM', 'DD-MMMM', 'D-MMMM',
    'MMM DD', 'MMM D', 'MMMM DD', 'MMMM D',
    'Do MMMM', 'Do MMM', 'MMM Do', 'MMMM Do',
  ];

  // Extract candidate date tokens.
  const tokenPatterns: RegExp[] = [
    /\b(\d{1,2}(?:st|nd|rd|th)?)\s+([A-Za-z]{3,})\s+(\d{4})\b/g,
    /\b(\d{1,2}(?:st|nd|rd|th)?)\s+([A-Za-z]{3,})\b/g,
    /\b([A-Za-z]{3,})\s+(\d{1,2}(?:st|nd|rd|th)?)\s+(\d{4})\b/g,
    /\b([A-Za-z]{3,})\s+(\d{1,2}(?:st|nd|rd|th)?)\b/g,
    /\b(\d{4})[-/](\d{1,2})[-/](\d{1,2})\b/g,
    /\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/g,
    /\b(\d{1,2})[/\-.](\d{1,2})\b/g,
  ];

  const rawTokens: { raw: string; index: number }[] = [];
  const seen = new Set<string>();
  for (const re of tokenPatterns) {
    let m: RegExpExecArray | null;
    re.lastIndex = 0;
    while ((m = re.exec(raw)) !== null) {
      const token = m[0];
      const key = `${m.index}:${token}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rawTokens.push({ raw: token, index: m.index });
    }
  }
  rawTokens.sort((a, b) => a.index - b.index);

  // Pass 1: parse each token. Track whether the token carried its own
  // year (explicit) or got assigned the current year tentatively.
  type Parsed = { m: moment.Moment; hasExplicitYear: boolean; tokenIndex: number };
  const parsedDates: Parsed[] = [];
  const unparsedIndices: number[] = [];

  rawTokens.forEach((tok, i) => {
    const withYear = moment(tok.raw, formatsWithYear, true);
    if (withYear.isValid()) {
      parsedDates.push({ m: withYear, hasExplicitYear: true, tokenIndex: i });
      return;
    }
    const withoutYear = moment(tok.raw, formatsNoYear, true);
    if (withoutYear.isValid()) {
      // Tentatively assign current year — will be adjusted below as a
      // coherent range, not per-date.
      withoutYear.year(today.year());
      parsedDates.push({ m: withoutYear, hasExplicitYear: false, tokenIndex: i });
      return;
    }
    unparsedIndices.push(i);
  });

  // Pass 2: bare-day tokens (e.g. "08" from "08 - 13 June"). Borrow
  // month/year from nearest parsed neighbour in token order.
  for (const i of unparsedIndices) {
    const bareDayMatch = /^(\d{1,2})(?:st|nd|rd|th)?$/.exec(rawTokens[i].raw.trim());
    if (!bareDayMatch) continue;
    const day = parseInt(bareDayMatch[1], 10);
    if (day < 1 || day > 31) continue;

    let neighbour: Parsed | null = null;
    for (const p of parsedDates) {
      if (p.tokenIndex > i) { neighbour = p; break; }
    }
    if (!neighbour) {
      for (let j = parsedDates.length - 1; j >= 0; j--) {
        if (parsedDates[j].tokenIndex < i) { neighbour = parsedDates[j]; break; }
      }
    }
    if (!neighbour) continue;

    const borrowed = neighbour.m.clone().date(day);
    if (borrowed.isValid()) {
      parsedDates.push({
        m: borrowed,
        hasExplicitYear: neighbour.hasExplicitYear,
        tokenIndex: i,
      });
    }
  }

  if (parsedDates.length === 0) return raw;

  // Sort by token position so year-crossing detection walks the input
  // in the order the user wrote it (which is chronological on almost
  // every chase list).
  parsedDates.sort((a, b) => a.tokenIndex - b.tokenIndex);

  // Cohesive-year rule: if ANY date has an explicit year, use the
  // earliest explicit year as the base for the no-year dates. Prevents
  // e.g. "8 June - 8 July 2026" from mixing 2026-inferred and
  // 2026-explicit years incorrectly.
  const explicitYears = parsedDates.filter((p) => p.hasExplicitYear).map((p) => p.m.year());
  if (explicitYears.length > 0) {
    const baseYear = Math.min(...explicitYears);
    parsedDates.forEach((p) => {
      if (!p.hasExplicitYear) p.m.year(baseYear);
    });
  }

  // Year-crossing walk: in token order, if a date lands BEFORE its
  // predecessor within the same year, treat it as a year rollover
  // (Dec → Jan). Adds +1 year to it and (transitively) to every date
  // after it that also lands before its predecessor.
  for (let i = 1; i < parsedDates.length; i++) {
    const prev = parsedDates[i - 1].m;
    const cur = parsedDates[i].m;
    if (cur.year() === prev.year() && cur.isBefore(prev)) {
      cur.add(1, 'year');
    }
  }

  // Roll-forward: if NO explicit years were provided AND the entire
  // range sits in the past, everything is next year's campaign. The
  // check is on the MAX date so a range straddling today (some past,
  // some future) is kept as-is — the future portion tells us we're
  // still in the current-year campaign.
  if (explicitYears.length === 0) {
    const maxSoFar = parsedDates.reduce(
      (max, p) => (p.m.isAfter(max) ? p.m : max),
      parsedDates[0].m,
    );
    if (maxSoFar.isBefore(today)) {
      parsedDates.forEach((p) => p.m.add(1, 'year'));
    }
  }

  // Sort chronologically, take min and max.
  const chronological = [...parsedDates].sort((a, b) => a.m.valueOf() - b.m.valueOf());
  const min = chronological[0].m;
  const max = chronological[chronological.length - 1].m;

  const OUTPUT = 'DD MMM YYYY';
  if (min.isSame(max)) return min.format(OUTPUT);
  return `${min.format(OUTPUT)} - ${max.format(OUTPUT)}`;
};
